import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Box, IconButton } from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import StopIcon from '@mui/icons-material/Stop';

interface Props {
  onSend: (message: string) => void;
  onStop: () => void;
  isLoading: boolean;
}

export default function ChatInput({ onSend, onStop, isLoading }: Props) {
  const [value, setValue] = useState('');
  const inputRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (!isLoading) inputRef.current?.focus();
  }, [isLoading]);

  const adjustHeight = useCallback(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 120)}px`;
  }, []);

  useEffect(() => {
    adjustHeight();
  }, [value, adjustHeight]);

  const handleSend = () => {
    const trimmed = value.trim();
    if (!trimmed || isLoading) return;
    onSend(trimmed);
    setValue('');
    if (inputRef.current) inputRef.current.style.height = 'auto';
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-end',
        gap: 1,
        p: 1.5,
        borderTop: '1px solid #e8ecf0',
        bgcolor: '#fff',
      }}
    >
      <Box
        component="textarea"
        ref={inputRef}
        value={value}
        onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setValue(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder={isLoading ? '응답 생성 중...' : '메시지를 입력하세요...'}
        disabled={isLoading}
        rows={1}
        sx={{
          flex: 1,
          border: '1px solid #d0d7de',
          borderRadius: '8px',
          padding: '10px 12px',
          fontSize: '0.85rem',
          fontFamily: 'inherit',
          resize: 'none',
          outline: 'none',
          lineHeight: 1.5,
          overflowY: 'auto',
          '&:focus': { borderColor: '#2D9BB2' },
          '&:disabled': { bgcolor: '#f9f9f9', color: '#aaa' },
        }}
      />
      {isLoading ? (
        <IconButton
          onClick={onStop}
          sx={{
            bgcolor: '#f44336',
            color: '#fff',
            width: 36,
            height: 36,
            '&:hover': { bgcolor: '#d32f2f' },
          }}
        >
          <StopIcon sx={{ fontSize: 18 }} />
        </IconButton>
      ) : (
        <IconButton
          onClick={handleSend}
          disabled={!value.trim()}
          sx={{
            bgcolor: value.trim() ? '#2D9BB2' : '#e0e0e0',
            color: '#fff',
            width: 36,
            height: 36,
            '&:hover': { bgcolor: '#1F7795' },
            '&.Mui-disabled': { bgcolor: '#e0e0e0', color: '#bbb' },
          }}
        >
          <SendIcon sx={{ fontSize: 18 }} />
        </IconButton>
      )}
    </Box>
  );
}
